import React, { MouseEvent, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import styles from "./ConfirmationDialog.module.css";

interface IDialogButton {
  label: string;
  value: string;
  variant: "primary" | "secondary";
  position: "left" | "right";
}

export interface ConfirmDialogProps {
  message: string;
  buttons: IDialogButton[];
  callback: (value: string) => void;
}

const defaultButtons: IDialogButton[] = [
  {
    label: "OK",
    position: "right",
    value: "ok",
    variant: "primary",
  },
  {
    label: "Cancel",
    position: "left",
    value: "cancel",
    variant: "secondary",
  },
];

const ConfirmationDialog: React.FC = () => {
  const [show, setShow] = useState<boolean>(false);
  const [message, setMessage] = useState<ConfirmDialogProps["message"]>("");
  const [leftButtons, setLeftButtons] = useState<ConfirmDialogProps["buttons"]>([]);
  const [rightButtons, setRightButtons] = useState<ConfirmDialogProps["buttons"]>([]);
  const [callbackFunction, setCallbackFunction] = useState<ConfirmDialogProps["callback"] | null>(null);

  useEffect(() => {
    Object.defineProperty(window, "showConfirmDialog", {
      value: (input: ConfirmDialogProps) => {
        const appWindow = typeof window.getAppWindow === "function" ? window.getAppWindow() : null;
        if (window === appWindow) {
          setState(input);
        } else if (window.top === window) {
          setState(input);
        } else {
          window.parent.showConfirmDialog(input);
        }
      },
      configurable: true,
    });
  }, []);

  const setState = (input: ConfirmDialogProps) => {
    const buttons = input.buttons?.length ? input.buttons : defaultButtons;
    setMessage(input.message || "");
    const _leftButtons = buttons.filter((button) => button.position === "left");
    const _rightButtons = buttons.filter((button) => button.position === "right");
    setLeftButtons(_leftButtons);
    setRightButtons(_rightButtons);
    setCallbackFunction(() => input.callback);
    setShow(true);
  };

  const onButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value;
    if (typeof callbackFunction === "function") {
      callbackFunction(value);
    }
    resetState();
  };

  const resetState = () => {
    setMessage("");
    setLeftButtons([]);
    setRightButtons([]);
    setCallbackFunction(null);
    setShow(false);
  };

  if (show) {
    return (
      <div className={styles.overlay}>
        <div className={styles.dialog}>
          <div className={styles.message} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />
          <div className={styles.buttonsContainer}>
            <div className={styles.section}>
              {leftButtons.map((item, i) => (
                <button
                  key={`dialog_button_left_${i}`}
                  onClick={onButtonClick}
                  value={item.value}
                  className={`confirm-dialog-button ${styles.button} ${
                    item.variant === "secondary" ? styles.secondaryButton : styles.primaryButton
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className={styles.section}>
              {rightButtons.map((item, i) => (
                <button
                  key={`dialog_button_right_${i}`}
                  onClick={onButtonClick}
                  value={item.value}
                  className={`confirm-dialog-button ${styles.button} ${
                    item.variant === "secondary" ? styles.secondaryButton : styles.primaryButton
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default ConfirmationDialog;
